import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete,
  DescriptionOutlined,
  NewReleasesOutlined,
  VideocamOutlined,
  WorkspacePremium,
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/EnterpriseAuthContext';
import { useApp } from '../context/AppContext';
import { formatDate } from '../utils/dateUtils';
import { getUserTier } from '../services/usageService';
import edgeFunctionService from '../services/edgeFunctionService';
import {
  getDefaultExtractionConfig,
  isExtractionConfigValid,
  extractionConfigToLegacyExtractionTypeArray,
  normalizeExtractionConfig,
} from '../utils/extractionTaxonomy';
import { analyzeTemplateQuality } from '../utils/templateQualityScore';
import { RECORD_MEETING_ANCHOR, sessionPath } from '../utils/navigation';
import RecentActivityFeed from './RecentActivityFeed';
import { SETTINGS_TAB_PRICING } from './SettingsDialog';
import { isMissingColumnError } from '../utils/supabaseSchemaFallback';
import HomeUnlinkedSessions from './HomeUnlinkedSessions';
import HomeLinkSessionDialog from './HomeLinkSessionDialog';
import { IconTile, ListSkeleton, PageContainer } from './pagekit';
import { useWorkspaceLayout } from '../context/WorkspaceLayoutContext';
import { isPresentationTemplateType } from './documents/documentMeta';
import { WHATS_NEW_LINK_PROPS } from './WhatsNewPage';
import { useTemplateContents } from '../hooks/useTemplateContents';
import { isTemplateContentLoaded } from '../utils/templateListSelect';
import { isAutopilotEnabled } from '../services/autopilotModel';
import ReadyForYou from './autopilot/ReadyForYou';

/**
 * Document templates (their content is loaded for quality scores; decks have none).
 * @param {object} template - Template.
 * @returns {boolean} True for document templates.
 */
const isDocumentTemplate = (template) => !isPresentationTemplateType(template.templateType);

/** Friendly message shown when a meeting link is malformed or on an unsupported platform. */
const UNSUPPORTED_MEETING_LINK_MESSAGE = "That doesn't look like a Teams, Meet or Zoom link.";

/**
 * Validate that a string is an http(s) URL on a supported meeting platform
 * (Microsoft Teams, Google Meet, or Zoom) and normalize it. Scheme-less input
 * like "meet.google.com/xxx-yyyy-zzz" — the form meeting UIs display — is
 * accepted and normalized to https.
 * @param {string} url - The URL to validate.
 * @returns {string|null} The normalized URL, or null when unsupported.
 */
const normalizeSupportedMeetingUrl = (url) => {
  let parsed;
  try {
    parsed = new URL(url);
  } catch (err) {
    try {
      parsed = new URL(`https://${url}`);
    } catch (retryErr) {
      return null;
    }
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  const supported =
    host === 'teams.microsoft.com' ||
    host === 'teams.live.com' ||
    host === 'meet.google.com' ||
    host === 'zoom.us' ||
    host.endsWith('.zoom.us');
  return supported ? parsed.href : null;
};

/**
 * Map a raw backend/bot error to a friendly, user-facing message.
 * Never surfaces raw JSON or status codes.
 * @param {string} rawMessage - The raw error message from the API.
 * @returns {string} A friendly error message.
 */
const toFriendlyBotError = (rawMessage) => {
  const text = String(rawMessage || '').toLowerCase();
  if (text.includes('meeting_url') || text.includes('malformed') || text.includes('unsupported')) {
    return UNSUPPORTED_MEETING_LINK_MESSAGE;
  }
  return "Couldn't send the bot — please check the link and try again.";
};

/**
 * Greeting for the local time of day.
 * @param {Date} now - Current time.
 * @returns {string} "Good morning" / "Good afternoon" / "Good evening".
 */
const greetingFor = (now) => {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

/**
 * White bordered action card (record a meeting, generate a document).
 * @param {object} props - Props.
 * @param {string} [props.id] - DOM id (anchor target).
 * @param {React.ReactNode} props.icon - Icon.
 * @param {'accent'|'danger'} props.tone - Icon tile tone.
 * @param {string} props.title - Title.
 * @param {string} props.description - Description.
 * @param {React.ReactNode} props.children - Controls.
 * @returns {JSX.Element} Card.
 */
function ActionCard({ id, icon, tone, title, description, children }) {
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card)',
        p: { xs: 2, sm: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        scrollMarginTop: 96,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        <IconTile tone={tone} size={40}>{icon}</IconTile>
        <Box sx={{ minWidth: 0 }}>
          <Typography id={id ? `${id}-title` : undefined} component="h2" sx={{ fontSize: 15, fontWeight: 600, mb: 0.25 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
      </Box>
      {children}
    </Box>
  );
}

/**
 * Home dashboard: greeting, record/generate actions, unlinked sessions triage, recent activity
 * and, with Meeting Autopilot on, "Ready for you" (the workspace metrics live in Admin > Overview).
 * @returns {JSX.Element} Home screen.
 */
const Home = () => {
  const { state: authState, actions: authActions } = useAuth();
  const { state: appState } = useApp();
  const navigate = useNavigate();
  const { openInky } = useWorkspaceLayout();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  // Bumped when Inky links meetings or creates customers/projects, so the unlinked list refetches.
  const [dataVersion, setDataVersion] = useState(0);
  useEffect(() => {
    const onChanged = (event) => {
      if (['session', 'customer', 'project'].includes(event?.detail?.kind)) setDataVersion((v) => v + 1);
    };
    window.addEventListener('quillai:data-changed', onChanged);
    return () => window.removeEventListener('quillai:data-changed', onChanged);
  }, []);
  const [error, setError] = useState('');
  const canManageTemplates = authActions.canManageTemplates();
  const autopilotEnabled = isAutopilotEnabled(authState.activeAccount);

  // Unlinked sessions state
  const [unlinkedSessions, setUnlinkedSessions] = useState([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [sessionToLink, setSessionToLink] = useState(null);
  const [linkCustomerId, setLinkCustomerId] = useState('');
  const [linkProjectId, setLinkProjectId] = useState('');
  const [linkExtractionConfig, setLinkExtractionConfig] = useState(() => getDefaultExtractionConfig());
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerProjects, setCustomerProjects] = useState([]);
  const [linkError, setLinkError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // Recall.ai "Record a meeting" state
  const [teamsUrl, setTeamsUrl] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState('');
  const [teamsSuccess, setTeamsSuccess] = useState('');
  const meetingInputRef = useRef(null);

  // "Generate a document" quick picks
  const [quickTemplateId, setQuickTemplateId] = useState('');
  const [quickProjectId, setQuickProjectId] = useState('');

  /** Stripe Checkout return: `?billing=success` | `?billing=canceled` */
  const [billingNotice, setBillingNotice] = useState(null);

  const userTier = getUserTier(authState.currentMembership, authActions.isAccountAdmin());
  const recallTierEnabled = ['pro-plus', 'enterprise'].includes(userTier);

  // The template list has no content: load it for document templates (managers only).
  useTemplateContents(appState.templates || [], { enabled: canManageTemplates, include: isDocumentTemplate });

  /** Quality of every document template (only computed for users who manage templates). */
  const templateQuality = useMemo(() => {
    const scores = new Map();
    if (!canManageTemplates) return scores;
    (appState.templates || []).forEach((t) => {
      if (isDocumentTemplate(t) && isTemplateContentLoaded(t)) scores.set(t.id, analyzeTemplateQuality(t.content || ''));
    });
    return scores;
  }, [appState.templates, canManageTemplates]);

  const templatesNeedingWork = useMemo(
    () => Array.from(templateQuality.values()).filter((q) => q.score < 80).length,
    [templateQuality]
  );

  const quickProjects = useMemo(
    () => [...(appState.projects || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [appState.projects]
  );

  /**
   * Derive customer projects from AppContext (owned+shared only).
   * Sync when linkCustomerId or appState.projects changes.
   */
  useEffect(() => {
    if (!linkCustomerId || !appState.projects) {
      setCustomerProjects([]);
      return;
    }
    const filtered = appState.projects
      .filter((p) => p.customer_id === linkCustomerId)
      .map((p) => ({ id: p.id, name: p.name }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setCustomerProjects(filtered);
  }, [linkCustomerId, appState.projects]);

  /**
   * Find a customer whose domain array contains the given domain string.
   * @param {string} domainToFind - Domain to search for.
   * @returns {object|null} The matching customer or null.
   */
  const findCustomerByDomain = useCallback((domainToFind) => {
    if (!domainToFind) return null;
    const normalized = domainToFind.toLowerCase().trim();
    return (
      allCustomers.find(
        (c) =>
          Array.isArray(c.domain) &&
          c.domain.some((d) => d.toLowerCase().trim() === normalized)
      ) || null
    );
  }, [allCustomers]);

  /**
   * Get the suggested customer for an unlinked session based on external domains.
   * @param {object} session - The session with source_metadata.
   * @returns {object|null} The suggested customer or null.
   */
  const getSuggestedCustomer = useCallback((session) => {
    const domains = session?.source_metadata?.external_domains || [];
    for (const domain of domains) {
      const match = findCustomerByDomain(domain);
      if (match) return match;
    }
    return null;
  }, [findCustomerByDomain]);

  /**
   * Stripe Checkout redirects to `/home?billing=success|canceled`; show feedback and strip query params.
   */
  useEffect(() => {
    const billing = searchParams.get('billing');
    if (billing !== 'success' && billing !== 'canceled') return;
    setBillingNotice(billing);
    const next = new URLSearchParams(searchParams);
    next.delete('billing');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // `/home#record-meeting` (the shell's "Record meeting" button): bring the card into view.
  useEffect(() => {
    if (location.hash !== `#${RECORD_MEETING_ANCHOR}`) return undefined;
    const timer = setTimeout(() => {
      document.getElementById(RECORD_MEETING_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      meetingInputRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => clearTimeout(timer);
  }, [location.hash, location.key]);

  /**
   * Send the Recall.ai bot to the provided meeting URL (Teams, Google Meet, or Zoom).
   */
  const handleSendBot = useCallback(async () => {
    const trimmedUrl = teamsUrl.trim();
    if (!trimmedUrl) {
      setTeamsError('Please enter a meeting URL (Teams, Google Meet, or Zoom).');
      return;
    }
    const normalizedUrl = normalizeSupportedMeetingUrl(trimmedUrl);
    if (!normalizedUrl) {
      setTeamsError(UNSUPPORTED_MEETING_LINK_MESSAGE);
      return;
    }
    setTeamsLoading(true);
    setTeamsError('');
    setTeamsSuccess('');
    try {
      const result = await edgeFunctionService.callFunction('recall-notetaker', {
        action: 'create-bot',
        meetingUrl: normalizedUrl,
        userId: authState.user?.id,
        accountId: authState.activeAccount?.id,
      });
      if (result?.success === false) {
        setTeamsError(toFriendlyBotError(result.error));
      } else {
        setTeamsSuccess('The bot is on its way. It joins the meeting and transcribes it automatically.');
        setTeamsUrl('');
      }
    } catch (err) {
      setTeamsError(toFriendlyBotError(err.message));
    } finally {
      setTeamsLoading(false);
    }
  }, [teamsUrl, authState.user?.id, authState.activeAccount?.id]);

  /**
   * Open the link dialog for an unlinked session.
   * Auto-suggests a customer based on domain matching.
   * @param {object} session - The session to link.
   */
  const handleOpenLinkDialog = useCallback((session) => {
    setSessionToLink(session);
    setLinkError('');
    setCustomerProjects([]);
    setLinkExtractionConfig(
      normalizeExtractionConfig(session.extraction_config, session.extraction_type)
    );

    // Auto-suggest customer based on domain matching
    const suggested = getSuggestedCustomer(session);
    if (suggested) {
      setLinkCustomerId(suggested.id);
    } else {
      setLinkCustomerId('');
    }
    setLinkProjectId('');
    setLinkDialogOpen(true);
  }, [getSuggestedCustomer]);

  /**
   * Open Inky with a request to match every unlinked session (up to 20 listed) to a customer and
   * project. The dock seeds its composer; the user reviews the request and sends it. Inky reads
   * the customers and projects with its data tools; linking stays a user action in this table.
   */
  const handleSuggestMatches = useCallback(() => {
    if (!unlinkedSessions.length) return;
    const lines = unlinkedSessions.slice(0, 20).map((s) => {
      const domains = (s.source_metadata?.external_domains || []).join(', ');
      const guess = getSuggestedCustomer(s);
      const hints = [formatDate(s.created_at), domains && `attendees from ${domains}`, guess && `domain matches ${guess.name}`]
        .filter(Boolean)
        .join('; ');
      return `- ${s.name || 'Untitled session'} (${hints})`;
    });
    const more = unlinkedSessions.length > 20 ? `\n(and ${unlinkedSessions.length - 20} more)` : '';
    const prompt =
      `Suggest which customer and project each of my ${unlinkedSessions.length} unlinked sessions belongs to. ` +
      'Check my customers and projects, give one match per session with a short reason, and say when nothing fits. ' +
      `I will link them from Home.\n${lines.join('\n')}${more}`;
    openInky({ prompt });
  }, [unlinkedSessions, getSuggestedCustomer, openInky]);

  /**
   * Link a session to a selected project.
   */
  const handleLinkSession = useCallback(async () => {
    if (!sessionToLink || !linkProjectId) {
      setLinkError('Please select both a customer and a project.');
      return;
    }

    const needsExtractionType = sessionToLink.source === 'recall' && !sessionToLink.extraction_type;
    if (needsExtractionType && !isExtractionConfigValid(linkExtractionConfig)) {
      setLinkError('Please complete extraction settings for this session.');
      return;
    }

    try {
      const updatePayload = { project_id: linkProjectId };
      if (needsExtractionType) {
        updatePayload.extraction_type = extractionConfigToLegacyExtractionTypeArray(
          linkExtractionConfig
        ).join(',');
        updatePayload.extraction_config = linkExtractionConfig;
      }

      let { error: updateErr } = await supabase
        .from('sessions')
        .update(updatePayload)
        .eq('id', sessionToLink.id);

      if (isMissingColumnError(updateErr, 'extraction_config') && updatePayload.extraction_config) {
        const { extraction_config: _omit, ...rest } = updatePayload;
        ({ error: updateErr } = await supabase
          .from('sessions')
          .update(rest)
          .eq('id', sessionToLink.id));
      }

      if (updateErr) throw updateErr;

      // Remove from unlinked list
      setUnlinkedSessions((prev) => prev.filter((s) => s.id !== sessionToLink.id));
      setLinkDialogOpen(false);
      setSessionToLink(null);
    } catch (err) {
      console.error('Error linking session:', err);
      setLinkError('Failed to link session: ' + err.message);
    }
  }, [sessionToLink, linkProjectId, linkExtractionConfig]);

  /**
   * Delete an unlinked session after confirmation.
   */
  const handleDeleteSession = useCallback(async () => {
    if (!sessionToDelete) return;

    try {
      const { error: deleteErr } = await supabase
        .from('sessions')
        .delete()
        .eq('id', sessionToDelete.id);

      if (deleteErr) throw deleteErr;

      setUnlinkedSessions((prev) => prev.filter((s) => s.id !== sessionToDelete.id));
      setDeleteConfirmOpen(false);
      setSessionToDelete(null);
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  }, [sessionToDelete]);

  useEffect(() => {
    const fetchData = async () => {
      if (!authState.activeAccount) return;

      setLoading(true);
      setError('');

      try {
        // Projects and templates come from AppContext (owned+shared).

        // All customers (with domain for matching) for the link dialog.
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, name, domain, created_at')
          .eq('account_id', authState.activeAccount.id)
          .order('created_at', { ascending: false });

        if (customersError) throw customersError;
        setAllCustomers(customersData || []);

        // Fetch unlinked sessions (retry without extraction_config if column not migrated)
        const unlinkedSelectFull =
          'id, name, summary, source, extraction_type, extraction_config, created_at, source_metadata';
        const unlinkedSelectLegacy =
          'id, name, summary, source, extraction_type, created_at, source_metadata';
        let { data: unlinkedData, error: unlinkedError } = await supabase
          .from('sessions')
          .select(unlinkedSelectFull)
          .eq('account_id', authState.activeAccount.id)
          .in('source', ['fathom', 'recall'])
          .is('project_id', null)
          .order('created_at', { ascending: false });
        if (isMissingColumnError(unlinkedError, 'extraction_config')) {
          ({ data: unlinkedData, error: unlinkedError } = await supabase
            .from('sessions')
            .select(unlinkedSelectLegacy)
            .eq('account_id', authState.activeAccount.id)
            .in('source', ['fathom', 'recall'])
            .is('project_id', null)
            .order('created_at', { ascending: false }));
        }

        if (unlinkedError) throw unlinkedError;
        setUnlinkedSessions(unlinkedData || []);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (authState.activeAccount) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [authState.activeAccount, dataVersion]);

  const now = new Date();
  const firstName = String(authState.user?.username || '').trim().split(/\s+/)[0];
  const attention = [
    unlinkedSessions.length > 0
      ? `${unlinkedSessions.length} recording${unlinkedSessions.length === 1 ? ' needs' : 's need'} a project`
      : null,
    templatesNeedingWork > 0
      ? `${templatesNeedingWork} template${templatesNeedingWork === 1 ? '' : 's'} could use a tune-up`
      : null,
  ].filter(Boolean);

  const handleQuickGenerate = () => {
    const project = quickProjects.find((p) => p.id === quickProjectId);
    const params = new URLSearchParams();
    if (quickTemplateId) params.set('templateId', quickTemplateId);
    if (project) {
      params.set('projectId', project.id);
      if (project.customer_id) params.set('customerId', project.customer_id);
    }
    const query = params.toString();
    navigate(`/generate${query ? `?${query}` : ''}`);
  };

  const pickerSx = { flex: '1 1 150px', minWidth: 0, '& .MuiOutlinedInput-root': { bgcolor: 'var(--q-bg-subtle)' } };

  return (
    <PageContainer sx={{ maxWidth: 1120, gap: 3 }}>
      <Snackbar
        open={!!billingNotice}
        autoHideDuration={8000}
        onClose={() => setBillingNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setBillingNotice(null)}
          severity={billingNotice === 'success' ? 'success' : 'info'}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {billingNotice === 'success'
            ? 'Payment successful! Your plan will update shortly after Stripe confirms.'
            : 'Checkout was canceled. You can subscribe anytime from Settings → Pricing.'}
        </Alert>
      </Snackbar>

      <Box component="header" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
            {now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </Typography>
          <Typography component="h1" sx={{ fontSize: { xs: 24, sm: 28 }, lineHeight: 1.2, fontWeight: 600, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>
            {greetingFor(now)}{firstName ? `, ${firstName}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }} role="status">
              {attention.length ? attention.join(' · ') : 'You are all caught up.'}
            </Typography>
          )}
          <Button
            component="a"
            {...WHATS_NEW_LINK_PROPS}
            size="small"
            startIcon={<NewReleasesOutlined sx={{ fontSize: '16px !important' }} />}
            aria-label="What's new (opens in a new tab)"
            sx={{
              flex: 'none',
              height: 28,
              px: 1.25,
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 'var(--q-radius-pill, 999px)',
              bgcolor: 'var(--q-accent-soft)',
              color: 'var(--q-accent-text)',
              '&:hover': { bgcolor: 'var(--q-accent-selected)' },
            }}
          >
            What&apos;s new
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 2 }}>
        <ActionCard
          id={RECORD_MEETING_ANCHOR}
          tone="danger"
          icon={<VideocamOutlined />}
          title="Record a meeting"
          description={recallTierEnabled
            ? 'Paste a Teams, Google Meet or Zoom link. Quill joins, records and transcribes.'
            : 'Automatically join Teams, Google Meet or Zoom meetings, record them and turn them into sessions.'}
        >
          {!recallTierEnabled ? (
            <Box>
              <Button
                variant="contained"
                startIcon={<WorkspacePremium />}
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent('quillai:open-settings', {
                      detail: { initialTab: SETTINGS_TAB_PRICING },
                    })
                  );
                }}
              >
                Upgrade to Pro Plus
              </Button>
            </Box>
          ) : teamsSuccess ? (
            <Alert severity="success" onClose={() => setTeamsSuccess('')}>{teamsSuccess}</Alert>
          ) : (
            <>
              <Box
                component="form"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendBot();
                }}
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
              >
                <TextField
                  inputRef={meetingInputRef}
                  placeholder="https://meet.google.com/…"
                  value={teamsUrl}
                  onChange={(e) => {
                    setTeamsUrl(e.target.value);
                    setTeamsError('');
                  }}
                  error={!!teamsError}
                  inputProps={{ 'aria-label': 'Meeting link', inputMode: 'url', autoComplete: 'off' }}
                  sx={{ flex: '1 1 200px', minWidth: 0, '& .MuiOutlinedInput-root': { bgcolor: 'var(--q-bg-subtle)' } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={teamsLoading || !teamsUrl.trim()}
                  startIcon={teamsLoading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {teamsLoading ? 'Sending…' : 'Send bot'}
                </Button>
              </Box>
              {teamsError && (
                <Alert severity="error" onClose={() => setTeamsError('')}>{teamsError}</Alert>
              )}
            </>
          )}
        </ActionCard>

        <ActionCard
          tone="accent"
          icon={<DescriptionOutlined />}
          title="Generate a document"
          description="Choose a template and project. Inky fills it from the project's requirements and sessions."
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              select
              label="Template"
              value={quickTemplateId}
              onChange={(e) => setQuickTemplateId(e.target.value)}
              sx={pickerSx}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
            >
              <MenuItem value="">Choose later</MenuItem>
              {(appState.templates || []).map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Project"
              value={quickProjectId}
              onChange={(e) => setQuickProjectId(e.target.value)}
              sx={pickerSx}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
            >
              <MenuItem value="">Choose later</MenuItem>
              {quickProjects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={handleQuickGenerate}>Generate</Button>
          </Box>
        </ActionCard>
      </Box>

      {loading ? (
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', overflow: 'hidden' }}>
          <ListSkeleton rows={3} label="Loading sessions" />
        </Box>
      ) : (
        <HomeUnlinkedSessions
          sessions={unlinkedSessions}
          getSuggestedCustomer={getSuggestedCustomer}
          onLink={handleOpenLinkDialog}
          onDeleteRequest={(session) => {
            setSessionToDelete(session);
            setDeleteConfirmOpen(true);
          }}
          onViewSession={(session) => navigate(sessionPath(session.id))}
          onSuggestMatches={handleSuggestMatches}
        />
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 2, alignItems: 'start' }}>
        <RecentActivityFeed loading={!appState.initialLoadComplete} canManageTemplates={canManageTemplates} />

        {autopilotEnabled && (
          <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
            {/* Phase 2 (calendar) adds "Upcoming meetings" here, above Ready for you. */}
            <ReadyForYou />
          </Box>
        )}
      </Box>

      <HomeLinkSessionDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        sessionToLink={sessionToLink}
        linkError={linkError}
        allCustomers={allCustomers}
        customerProjects={customerProjects}
        linkCustomerId={linkCustomerId}
        onCustomerChange={(id) => {
          setLinkCustomerId(id);
          setLinkProjectId('');
        }}
        linkProjectId={linkProjectId}
        onProjectChange={setLinkProjectId}
        linkExtractionConfig={linkExtractionConfig}
        onExtractionConfigChange={setLinkExtractionConfig}
        getSuggestedCustomer={getSuggestedCustomer}
        onSubmit={handleLinkSession}
      />

      {/* Delete Session Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle>Delete session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            &quot;{sessionToDelete?.name}&quot; and its transcript will be permanently deleted. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSession} variant="contained" color="error" startIcon={<Delete />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Home;
